<template>
  <div
    class="file-selection-card"
    :class="{ 'has-file': hasFile }"
    @click="onCardClick"
  >
    <div class="file-icon" :class="{ 'has-preview': hasPreview }">
      <!-- Show preview for image files -->
      <img
        v-if="hasFile && isImage && file.url"
        :src="file.url"
        class="preview-image"
        alt="Image preview"
      />
      <!-- Show preview for video files -->
      <div
        v-else-if="hasFile && isVideo && file.url"
        class="video-preview"
      >
        <video
          v-if="videoThumbnailFailed"
          class="preview-video"
          :src="file.url"
          preload="metadata"
        ></video>
        <div v-else class="video-thumbnail">
          <img
            v-if="videoThumbnailUrl"
            :src="videoThumbnailUrl"
            class="thumbnail-image"
            alt="Video thumbnail"
          />
          <feather-icon
            v-else
            icon="VideoIcon"
            size="30"
          />
          <div class="play-icon-overlay">
            <feather-icon icon="PlayIcon" size="16" />
          </div>
        </div>
      </div>
      <!-- Show preview for audio files -->
      <div
        v-else-if="hasFile && isVoice && file.url"
        class="audio-preview"
      >
        <div class="audio-thumbnail">
          <div class="audio-waveform">
            <div v-for="i in 4" :key="i" class="waveform-bar" :style="{ height: `${20 + Math.random() * 30}%` }"></div>
          </div>
          <div class="play-icon-overlay">
            <feather-icon icon="PlayIcon" size="16" />
          </div>
        </div>
      </div>
      <!-- Default icon for other file types or when no preview is available -->
      <feather-icon
        v-else
        :icon="fileIcon"
        size="30"
      />
    </div>
    <div class="file-info">
      <span class="file-title">{{ title }}</span>
      <span
        v-if="hasFile"
        class="file-name"
      >{{ fileName }}</span>
      <span
        v-else
        class="file-hint"
      >{{ $t('field.selectFromChat') }}</span>
    </div>
    <div
      v-if="hasFile"
      class="file-actions"
    >
      <feather-icon
        icon="XIcon"
        size="16"
        class="remove-icon"
        @click.stop="onRemove"
      />
    </div>
  </div>
</template>

<script>
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'FileSelectionCard',

  components: {
    FeatherIcon,
  },

  props: {
    // Type of file (photo, video, voice)
    type: {
      type: String,
      required: true,
      validator: value => ['photo', 'video', 'voice'].includes(value),
    },
    // File object if selected
    file: {
      type: Object,
      default: null,
    },
    // Title to display
    title: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      videoThumbnailUrl: null,
      videoThumbnailFailed: false,
    }
  },

  computed: {
    hasFile() {
      return !!this.file
    },
    fileName() {
      return this.file?.originalName || ''
    },
    fileIcon() {
      const iconMap = {
        photo: 'ImageIcon',
        video: 'VideoIcon',
        voice: 'MicIcon',
      }
      return iconMap[this.type] || 'FileIcon'
    },
    isImage() {
      return this.type === 'photo' && this.file?.mimeType?.startsWith('image/')
    },
    isVideo() {
      return this.type === 'video' && this.file?.mimeType?.startsWith('video/')
    },
    isVoice() {
      return this.type === 'voice' && this.file?.mimeType?.startsWith('audio/')
    },
    hasPreview() {
      return (this.isImage || this.isVideo || this.isVoice) && this.file?.url
    }
  },

  watch: {
    'file.url': {
      immediate: true,
      handler(newUrl) {
        if (this.isVideo && newUrl) {
          this.generateVideoThumbnail(newUrl)
        }
      }
    }
  },

  methods: {
    onCardClick() {
      this.$emit('select')
    },
    onRemove() {
      this.$emit('remove')
    },
    generateVideoThumbnail(videoUrl) {
      if (!videoUrl) return

      // Create a video element to generate the thumbnail
      const video = document.createElement('video')
      video.style.display = 'none'
      video.crossOrigin = 'anonymous'
      video.src = videoUrl
      video.muted = true
      video.preload = 'metadata'

      // Set a timeout to handle cases where the video doesn't load properly
      const timeoutId = setTimeout(() => {
        console.warn('Thumbnail generation timed out')
        video.remove()
        this.videoThumbnailFailed = true
      }, 5000) // 5 second timeout

      // When metadata is loaded, seek to a point in the video
      video.onloadedmetadata = () => {
        try {
          // Seek to 25% of the video duration for a good thumbnail
          const seekTime = Math.min(video.duration * 0.25, 5) // Cap at 5 seconds

          // For very short videos, use the middle
          if (video.duration < 2) {
            video.currentTime = video.duration / 2
          } else {
            video.currentTime = seekTime
          }
        } catch (error) {
          console.error('Error seeking video:', error)
          // Try a fixed position as fallback
          try {
            video.currentTime = 1.0 // Try 1 second in
          } catch (e) {
            this.videoThumbnailFailed = true
            clearTimeout(timeoutId)
            video.remove()
          }
        }
      }

      // When the frame at the seek point is loaded
      video.onseeked = () => {
        try {
          clearTimeout(timeoutId) // Clear the timeout

          // Create a canvas to capture the frame
          const canvas = document.createElement('canvas')

          // Set canvas dimensions to create a square thumbnail
          canvas.width = 120 // Higher resolution for better quality
          canvas.height = 120

          // Draw the video frame to the canvas with cropping to square
          const ctx = canvas.getContext('2d')

          // Fill with background color first
          ctx.fillStyle = '#e2e8f0'
          ctx.fillRect(0, 0, canvas.width, canvas.height)

          // Draw the video frame centered and cropped
          if (video.videoWidth && video.videoHeight) {
            // Calculate the source rectangle for cropping
            let sx = 0
            let sy = 0
            let sWidth = 0
            let sHeight = 0

            if (video.videoWidth > video.videoHeight) {
              // Landscape video - crop sides
              sWidth = video.videoHeight
              sHeight = video.videoHeight
              sx = (video.videoWidth - video.videoHeight) / 2
              sy = 0
            } else {
              // Portrait or square video - crop top/bottom
              sWidth = video.videoWidth
              sHeight = video.videoWidth
              sx = 0
              sy = (video.videoHeight - video.videoWidth) / 2
            }

            // Draw the cropped video frame
            ctx.drawImage(
              video,
              sx, sy, sWidth, sHeight, // Source rectangle
              0, 0, canvas.width, canvas.height, // Destination rectangle
            )
          }

          // Convert canvas to image with high quality
          this.videoThumbnailUrl = canvas.toDataURL('image/jpeg', 0.9)

          // Clean up
          video.remove()
        } catch (error) {
          console.error('Error generating thumbnail:', error)
          clearTimeout(timeoutId) // Clear the timeout
          video.remove()
          this.videoThumbnailFailed = true
        }
      }

      // Handle errors
      video.onerror = () => {
        console.error('Error loading video for thumbnail generation')
        clearTimeout(timeoutId) // Clear the timeout
        video.remove()
        this.videoThumbnailFailed = true
      }

      // Start loading the video
      document.body.appendChild(video)
    }
  },
}
</script>

<style lang="scss" scoped>
.file-selection-card {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
  cursor: pointer;
  height: 80px;

  &.has-file {
    background-color: rgba(141, 81, 133, 0.08);
    border-color: #8d5185;
  }

  .file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 8px;
    background-color: #e9ecef;
    margin-right: 0.75rem;
    color: #6c757d;
    overflow: hidden;
    position: relative;

    .has-file & {
      background-color: #8d5185;
      color: white;
    }

    &.has-preview {
      background-color: transparent;
    }

    .preview-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .video-preview {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }

    .video-thumbnail {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #e2e8f0;

      .thumbnail-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .preview-video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .audio-preview {
      width: 100%;
      height: 100%;
      position: relative;
      overflow: hidden;
    }

    .audio-thumbnail {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #e2e8f0;

      .audio-waveform {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 70%;
        height: 60%;

        .waveform-bar {
          width: 15%;
          background-color: #8d5185;
          border-radius: 2px;
        }
      }
    }

    .play-icon-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.3);
      color: white;
      z-index: 2;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 60%);
        z-index: -1;
      }
    }
  }

  .file-info {
    flex: 1;
    overflow: hidden;
    min-height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .file-title {
      display: block;
      font-weight: 600;
      color: #495057;
    }

    .file-hint {
      display: block;
      font-size: 0.75rem;
      color: #6c757d;
    }

    .file-name {
      display: block;
      font-size: 0.75rem;
      color: var(--primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .file-actions {
    margin-left: 0.5rem;

    .remove-icon {
      cursor: pointer;
      color: #dc3545;

      &:hover {
        color: darken(#dc3545, 10%);
      }
    }
  }
}
</style>

